import { Component, Prop, h, Element, State, Method } from '@stencil/core';
import { ACTION_REGISTRY, type ActionName } from '../../../actions';
import type { ActionContext } from '../../../actions/types';

const COMPONENT_NAME = 'me-button';

// Inline SVG icons for critical above-the-fold elements (LCP optimization)
// Eliminates network requests and lazy loading overhead
const INLINE_ICONS: Record<string, () => any> = {
  'arrows--chevron-down': () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class={`${COMPONENT_NAME}__icon`}
      aria-hidden="true"
    >
      <path d="M16.293 8.29297L12 12.586L7.70697 8.29297L6.29297 9.70697L12 15.414L17.707 9.70697L16.293 8.29297Z" fill="currentColor" />
    </svg>
  ),
  'arrows--chevron-right': () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class={`${COMPONENT_NAME}__icon`}
      aria-hidden="true"
    >
      <path d="M9.70697 16.9496L15.414 11.2426L9.70697 5.53564L8.29297 6.94964L12.586 11.2426L8.29297 15.5356L9.70697 16.9496Z" fill="currentColor" />
    </svg>
  ),
  'interface--search': () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class={`${COMPONENT_NAME}__icon`}
      aria-hidden="true"
    >
      <path
        d="M10 18C11.775 17.9996 13.4988 17.4054 14.897 16.312L19.293 20.708L20.707 19.294L16.311 14.898C17.405 13.4997 17.9996 11.7754 18 10C18 5.589 14.411 2 10 2C5.589 2 2 5.589 2 10C2 14.411 5.589 18 10 18ZM10 4C13.309 4 16 6.691 16 10C16 13.309 13.309 16 10 16C6.691 16 4 13.309 4 10C4 6.691 6.691 4 10 4Z"
        fill="currentColor"
      />
    </svg>
  ),
};

/**
 * A flexible and accessible button component with support for native tags (`button`, `a`, `input`, `span`),
 * icons, keyboard interaction, and appearance modifiers.
 *
 * @category Elements
 * @slot default - {required}, string
 * @example
 *   <me-button variant="primary">Button</me-button>
 */
@Component({
  tag: 'me-button',
  styleUrl: 'index.scss',
  shadow: false,
})
export class Button {
  @Element() host: HTMLElement;

  /**
   * Which HTML tag to render.
   * Common values: `button`, `a`, `span`, `input`.
   * @default span
   */
  @Prop() tag: 'span' | 'a' | 'button' | 'input' = 'span';

  /**
   * Button visual style variant.
   * Options: `primary` (default), `secondary`, `tertiary`, `detailed`, `ghost`.
   * @default primary
   */
  @Prop() variant: 'primary' | 'secondary' | 'tertiary' | 'detailed' | 'ghost' = 'primary';

  /**
   * Size modifier.
   * Options: `sm`, `md`
   * @default md
   */
  @Prop() size: 'sm' | 'md' = 'md';

  /**
   * HTML `type` attribute for `<button>` or `<input>`.
   * Common values: `submit`, `button`.
   */
  @Prop() type?: 'button' | 'submit';

  /**
   * If provided, the button renders as an `<a>` with this `href`.
   */
  @Prop() href?: string;

  /**
   * Disables the button.
   * Applies `disabled` or `aria-disabled` depending on tag.
   */
  @Prop() disabled: boolean = false;

  /**
   * Native `title` attribute — shown as tooltip on hover.
   */
  @Prop() linkTitle?: string;

  /**
   * Accessibility label for screen readers.
   * Especially useful for icon-only buttons.
   */
  @Prop() ariaLabel?: string;

  /**
   * Icon name to render inside the button via `<me-icon>`.
   */
  @Prop() icon?: string;

  /**
   * Position of icon relative to text.
   * Options: `before`, `after`, `only`.
   * @default before
   */
  @Prop() iconPosition: 'before' | 'after' | 'only' = 'before';

  /**
   * Whether to preload the icon immediately (skip lazy loading).
   * Useful for icons in initially hidden elements.
   * @default false
   * @deprecated Use iconLazy="false" instead
   */
  @Prop() iconPreload?: boolean = false;

  /**
   * Enable lazy loading for the icon (default: true). Accepts boolean or string ('true'/'false').
   * When true, icons load only when near viewport. When false, loads immediately.
   * @default true
   */
  @Prop() iconLazy: boolean | string = true;

  /**
   * Inline the icon SVG during server-side rendering (SSR).
   * When true, the icon SVG is fetched from CDN on the server and inlined into HTML,
   * eliminating network requests for critical above-the-fold icons.
   * Only affects SSR - on client-side, behaves like iconLazy="false".
   * @default false
   */
  @Prop() iconInline?: boolean = false;

  /**
   * Appearance modifier based on background.
   * - `light` (default): use on light background
   * - `dark`: use on dark background
   * @default light
   */
  @Prop() appearance: 'dark' | 'light' = 'light';

  /**
   * Optional native click handler.
   * Especially useful in non-framework setups.
   */
  @Prop() clickHandler?: (event: MouseEvent) => void;

  /**
   * Optional function to forward ref to inner native element.
   * Useful for modal focus traps, tooltips, etc.
   */
  @Prop() elRef?: (el: HTMLElement | null) => void;

  /**
   * Array of action names to execute on click.
   * Format: 'category:action-name'
   * Examples: ['ui:open-modal', 'api:fetch-data']
   * @enumFrom ACTION_REGISTRY
   */
  @Prop({ reflect: true }) triggerActions?: ActionName[] = [];

  /**
   * Additional data to pass to actions
   */
  @Prop({ reflect: true }) actionData?: Record<string, any> = {};

  /**
   * Stores computed label (for input[type="button"])
   */
  @State() computedLabel = '';

  /**
   * True if slot has any visible content (for icon-only logic)
   */
  @State() hasSlotContent = false;

  /**
   * Specifies the target attribute for <a> tag (e.g. _blank, _self)
   */
  @Prop() target?: string;
  /**
   * Specifies the rel attribute for <a> tag (e.g. noopener, noreferrer)
   */
  @Prop() rel?: string;

  /** Ref to rendered native element */
  private renderedEl!: HTMLElement;

  /**
   * Returns the icon position (attribute takes precedence for SSR consistency).
   * When the host has a valid icon-position attribute, use it; otherwise use the prop.
   */
  private getIconPosition(): 'before' | 'after' | 'only' {
    const attr = this.host.getAttribute('icon-position');
    if (attr === 'after' || attr === 'only' || attr === 'before') return attr;
    return this.iconPosition;
  }

  /**
   * Returns the computed aria-label for the button
   */
  private getComputedAriaLabel(): string | undefined {
    if (this.ariaLabel) return this.ariaLabel;
    if (this.icon && this.getIconPosition() === 'only') {
      return this.linkTitle || this.host.textContent?.trim() || this.computedLabel;
    }
    return undefined;
  }

  /**
   * Pre-render hook: detects slot content, handles icon-only cleanup,
   * and sets computed label for input buttons.
   */
  componentWillRender() {
    const rawText = this.host.textContent?.trim();
    this.hasSlotContent = !!rawText;

    if (this.tag === 'input') {
      this.computedLabel = rawText || '';
    }

    if (this.icon && this.getIconPosition() === 'only') {
      if (rawText) {
        this.host.innerHTML = '';
      }
      this.hasSlotContent = false;
    }
  }

  /**
   * After-render hook: adds keyboard interaction if tag is `span`.
   */
  componentDidLoad() {
    this.updateSpanKeyListener();
    this.attachClickListener();
  }

  componentDidUpdate() {
    this.updateSpanKeyListener();
    this.attachClickListener();
  }

  disconnectedCallback() {
    this.detachClickListener();
    this.host.removeEventListener('keydown', this.handleKeyDown);
  }

  private updateSpanKeyListener() {
    if (this.tag === 'span') {
      if (!this.disabled) {
        this.host.addEventListener('keydown', this.handleKeyDown);
      } else {
        this.host.removeEventListener('keydown', this.handleKeyDown);
      }
    }
  }

  /**
   * Returns the inner HTML element (<button>, <a>, etc.).
   * Can be used by parent components to control focus or attributes.
   */
  @Method()
  async getInnerElement(): Promise<HTMLElement | undefined> {
    return this.renderedEl;
  }

  /**
   * Execute all registered actions
   */
  private async executeActions(event: MouseEvent): Promise<void> {
    if (!this.triggerActions?.length) return;

    try {
      for (const actionName of this.triggerActions) {
        await this.executeSingleAction(actionName, event);
      }
    } catch (error) {
      console.error('Action execution failed:', error);
    }
  }

  /**
   * Execute single action
   */
  private async executeSingleAction(actionName: ActionName, event: MouseEvent): Promise<void> {
    const actionLoader = ACTION_REGISTRY[actionName];

    if (!actionLoader) {
      console.warn(`Action "${actionName}" not found in registry`);
      return;
    }

    try {
      const actionModule = await actionLoader();
      const ActionClass = actionModule.default;
      const action = new ActionClass();

      const context: ActionContext = {
        event,
        element: this.renderedEl,
        component: this,
        data: this.actionData,
      };

      await action.execute(context);
    } catch (error) {
      console.error(`Failed to execute action "${actionName}":`, error);
      throw error;
    }
  }

  /**
   * Enhanced click handler
   */
  private handleClick = async (event: MouseEvent) => {
    if (this.disabled) {
      event.preventDefault();
      return;
    }
    // Execute custom actions if any
    if (this.triggerActions?.length) {
      await this.executeActions(event);
    }

    // Execute standard click handler if any
    if (this.clickHandler) {
      this.clickHandler(event);
    }
  };

  /**
   * Keyboard accessibility handler for <span> buttons.
   */
  private handleKeyDown = (event: KeyboardEvent) => {
    if (this.disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      this.host.click();
      event.preventDefault();
    }
  };

  /**
   * Computes dynamic BEM-based class list based on props and slot state.
   */
  private getClassList(): string {
    const hasIcon = !!this.icon;
    const iconPos = this.getIconPosition();
    const iconOnly = hasIcon && iconPos === 'only' && !this.hasSlotContent;
    const hostClass = this.host.getAttribute('class') ?? '';

    const classes = {
      [COMPONENT_NAME]: true,
      [`${COMPONENT_NAME}--${this.variant}`]: true,
      [`${COMPONENT_NAME}--${this.size}`]: this.size !== 'md',
      [`${COMPONENT_NAME}--disabled`]: this.disabled,
      [`${COMPONENT_NAME}--icon-${iconPos}`]: hasIcon,
      [`${COMPONENT_NAME}--icon-only`]: iconOnly,
      [`${COMPONENT_NAME}--${this.appearance}`]: this.appearance !== 'light',
      [hostClass]: hostClass,
    };

    return Object.entries(classes)
      .filter(([, value]) => value)
      .map(([key]) => key)
      .join(' ');
  }

  /**
   * Generates a set of dynamic attributes depending on tag and props.
   * Handles accessibility and native tag behavior.
   */
  private getAttributes(tag: string): Record<string, string | number | boolean | EventListener | undefined> {
    const attrs = {
      'class': this.getClassList(),
      'title': this.linkTitle,
      'aria-label': this.getComputedAriaLabel(),
    } as const;

    if (tag === 'span') {
      Object.assign(attrs, {
        role: 'button',
        tabIndex: this.disabled ? -1 : 0,
        ...(this.disabled && { 'aria-disabled': 'true' }),
      });
    }

    if (tag === 'a') {
      // Always provide href for SEO - if href is not provided but tag="a" is set, use empty string
      // Empty href is better than missing href for crawlers (they can still scan the link)
      const hrefValue = this.href !== undefined && this.href !== null ? this.href : '';
      Object.assign(attrs, {
        href: hrefValue,
        target: this.target,
        rel: this.rel,
        ...(this.disabled && {
          'aria-disabled': 'true',
          'tabIndex': -1,
        }),
      });
    }

    if (tag === 'button' || tag === 'input') {
      Object.assign(attrs, { disabled: this.disabled });
    }

    if (tag === 'button' && this.type) {
      Object.assign(attrs, { type: this.type });
    }

    if (tag === 'input') {
      Object.assign(attrs, {
        type: this.type || 'button',
        value: this.computedLabel,
      });
    }

    return attrs;
  }

  /**
   * Attach click listener to the rendered element instead of inline onclick
   */
  private attachClickListener() {
    if (!this.renderedEl) return;
    this.detachClickListener();
    this.renderedEl.addEventListener('click', this.handleClick);
  }

  /**
   * Detach click listener to avoid duplicates and memory leaks
   */
  private detachClickListener() {
    if (!this.renderedEl) return;
    this.renderedEl.removeEventListener('click', this.handleClick);
  }

  /**
   * Conditionally renders the icon if provided and matches the current position.
   * Uses inline SVG for critical icons to optimize LCP/FCP.
   */
  private renderIcon(position: 'before' | 'after' | 'only') {
    if (!this.icon || this.getIconPosition() !== position) return null;

    // Check if icon has inline version for LCP optimization
    const InlineIcon = INLINE_ICONS[this.icon];
    if (InlineIcon) {
      return <InlineIcon />;
    }

    // Fallback to me-icon component for non-critical icons
    return <me-icon name={this.icon} class={`${COMPONENT_NAME}__icon`} isPreload={this.iconPreload} lazy={this.iconLazy} inline={this.iconInline} />;
  }

  /**
   * Main render function.
   * Dynamically renders the desired HTML tag with proper attributes and content.
   */
  render() {
    // If tag="a" is explicitly set, always render as <a> for SEO
    // Otherwise, use href to determine tag
    const Tag = this.tag === 'a' ? 'a' : this.href ? 'a' : this.tag || (this.type ? 'button' : 'span');
    const attrs = this.getAttributes(Tag);
    const isIconOnly = this.icon && this.getIconPosition() === 'only' && !this.hasSlotContent;

    return Tag === 'input' ? (
      <Tag
        ref={el => {
          this.renderedEl = el;
          this.elRef?.(el);
        }}
        {...attrs}
      />
    ) : (
      <Tag
        ref={el => {
          this.renderedEl = el;
          this.elRef?.(el);
        }}
        {...attrs}
      >
        {!isIconOnly && this.renderIcon('before')}
        {!isIconOnly && <slot />}
        {!isIconOnly && this.renderIcon('after')}
        {isIconOnly && this.renderIcon('only')}
      </Tag>
    );
  }
}
